import { announce } from './announcer'
import { DND_DRAG_HANDLE_ATTR, DND_DROPPABLE_ATTR } from './attributes'
import { clearDropTarget, currentDropTarget, type DropTarget, enterDropTarget } from './dropTarget'
import { emitDrop, isDropValid } from './registry'
import { type DndScope, type Gesture, inScope, kitDroppableSelector, nearestWithAttribute, readDropId } from './scope'
import { endGesture, focusIfReachable, type KeyboardDrag, runningGesture, setBodyDragging, setRunningGesture } from './session'

// Keyboard path: Space or Enter lifts, arrows move between the kit's
// droppables in scope, Space or Enter drops, Escape cancels. Every step is
// spoken through the live region.

function isDropKey(event: KeyboardEvent): boolean {
    return event.key === ' ' || event.key === 'Enter'
}

function isSourceOrHandle(target: Element, sourceElement: HTMLElement): boolean {
    if (target === sourceElement) return true
    const handle = target.closest(`[${DND_DRAG_HANDLE_ATTR}]`)
    return handle !== null && sourceElement.contains(handle)
}

// Keys pressed in a nested control (an input, a remove button) belong to that
// control. A held key only lifts once: its auto-repeats are not new presses.
export function isLiftPress(event: KeyboardEvent, sourceElement: HTMLElement): boolean {
    if (!isDropKey(event) || event.repeat) return false
    const target = event.target
    return !(target instanceof Element) || isSourceOrHandle(target, sourceElement)
}

// Droppables without an id still need a spoken name.
function targetLabel(target: DropTarget): string {
    return target.dropId ?? target.element.getAttribute('aria-label') ?? 'drop target'
}

type CandidateSnapshot = {
    /** Unfiltered query result in document order; the next collect validates against it. */
    candidateQuery: Element[]
    candidates: HTMLElement[]
}

function sameQuery(found: NodeListOf<Element>, query: Element[]): boolean {
    if (found.length !== query.length) return false
    for (let index = 0; index < found.length; index += 1) {
        if (found[index] !== query[index]) return false
    }
    return true
}

function collectKeyboardCandidates(scope: DndScope, previous: CandidateSnapshot | null): CandidateSnapshot {
    // One O(N) query per lift and per arrow key, never per render. Keypresses
    // are rare enough that re-reading is cheaper than tracking DOM moves.
    const found = (scope.zone ?? document).querySelectorAll(kitDroppableSelector(scope.kitId))
    // Same match list as the previous collect: the same elements resolve to the
    // same scope verdicts, so reuse the filtered candidates. Any add, remove, or
    // reorder changes the list and re-runs the filter, so slots always match the
    // current DOM. Zone structure is assumed stable across the gesture: zones do
    // not mount or unmount mid-drag, and React zone restructuring recreates the
    // droppables, which the list comparison catches.
    if (previous !== null && sameQuery(found, previous.candidateQuery)) return previous
    const candidates: HTMLElement[] = []
    for (const node of found) {
        // Same zone rule as the pointer hit-test.
        if (node instanceof HTMLElement && inScope(node, scope)) candidates.push(node)
    }
    return { candidateQuery: [...found], candidates }
}

function focusCandidate(element: HTMLElement): void {
    // Plain divs just scroll without stealing focus.
    focusIfReachable(element)
    if (typeof element.scrollIntoView === 'function') element.scrollIntoView({ block: 'nearest' })
}

function selectKeyboardCandidate(drag: KeyboardDrag, index: number): void {
    const count = drag.candidates.length
    if (count === 0) return
    // Wrap-around so arrowing past either end cycles instead of dead-ending.
    const wrapped = ((index % count) + count) % count
    const previousIndex = drag.candidateIndex
    drag.candidateIndex = wrapped
    const element = drag.candidates[wrapped]
    // Wrap keeps this in range; the check is for noUncheckedIndexedAccess.
    if (element === undefined) return
    // A re-render may have moved the hovered element into another slot since
    // it was selected; selecting it in that new slot must emit again.
    if (currentDropTarget()?.element === element && wrapped === previousIndex) return
    // Silent clear: the single transition below reports the new target.
    clearDropTarget(drag, false)
    // No pointer → no insertion edge (attr stays unset; event edge is null).
    const dropId = readDropId(element)
    enterDropTarget(drag, { element, dropId, valid: isDropValid(drag, element, dropId), edge: null })
    focusCandidate(element)
}

function candidateAnnouncement(drag: KeyboardDrag): string {
    const target = currentDropTarget()
    if (target === null) return ''
    const position = `${drag.candidateIndex + 1} of ${drag.candidates.length}`
    if (target.valid) return `Over ${targetLabel(target)}, ${position}. Press Space to drop.`
    return `Over ${targetLabel(target)}, ${position}, cannot drop here.`
}

function moveKeyboardCandidate(drag: KeyboardDrag, pick: (index: number, count: number) => number): void {
    // Re-read so slots match the DOM after a re-render moved droppables.
    const collected = collectKeyboardCandidates(drag.scope, drag)
    drag.candidates = collected.candidates
    drag.candidateQuery = collected.candidateQuery
    selectKeyboardCandidate(drag, pick(drag.candidateIndex, drag.candidates.length))
    // Empty while no candidate is marked (zero-candidate lift): keep the lift message.
    const message = candidateAnnouncement(drag)
    if (message !== '') announce(message)
}

// Lifting starts where the item already is: a draggable inside a droppable
// starts on that droppable, so Space-Space drops it back where it was.
function ownCandidateIndex(gesture: Gesture, candidates: HTMLElement[]): number {
    const own = nearestWithAttribute(gesture.sourceElement, DND_DROPPABLE_ATTR, gesture.scope.kitId)
    const index = own instanceof HTMLElement ? candidates.indexOf(own) : -1
    return index === -1 ? 0 : index
}

export function startKeyboardDrag(gesture: Gesture): void {
    const collected = collectKeyboardCandidates(gesture.scope, null)
    setBodyDragging(true)
    const startIndex = ownCandidateIndex(gesture, collected.candidates)
    const drag: KeyboardDrag = {
        ...gesture,
        kind: 'keyboard',
        candidates: collected.candidates,
        candidateQuery: collected.candidateQuery,
        candidateIndex: startIndex,
    }
    setRunningGesture(drag)
    if (collected.candidates.length === 0) {
        announce('Lifted item. No drop targets here. Press Escape to cancel.')
        return
    }
    selectKeyboardCandidate(drag, startIndex)
    announce(`Lifted item. ${candidateAnnouncement(drag)} Arrow keys move between targets, Escape cancels.`)
}

function dropKeyboardDrag(drag: KeyboardDrag): void {
    const target = currentDropTarget()
    if (target === null) {
        announce('No drop target selected.')
        return
    }
    const label = targetLabel(target)
    // Re-check at drop time: app state may have changed since the move.
    if (!isDropValid(drag, target.element, target.dropId)) {
        // Stay lifted so the user can arrow elsewhere instead of restarting.
        announce(`Cannot drop on ${label}.`)
        return
    }
    // A throwing onDrop must still end the gesture, or the item stays lifted
    // with the window listeners attached.
    try {
        emitDrop(drag, target.element, target.dropId, null)
    } finally {
        endGesture()
    }
    announce(`Dropped on ${label}.`)
}

function onKeyboardGestureKey(drag: KeyboardDrag, event: KeyboardEvent): void {
    switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
            event.preventDefault()
            moveKeyboardCandidate(drag, index => index + 1)
            break
        case 'ArrowUp':
        case 'ArrowLeft':
            event.preventDefault()
            moveKeyboardCandidate(drag, index => index - 1)
            break
        case 'Home':
            event.preventDefault()
            moveKeyboardCandidate(drag, () => 0)
            break
        case 'End':
            event.preventDefault()
            moveKeyboardCandidate(drag, (_, count) => count - 1)
            break
        case ' ':
        case 'Enter':
            event.preventDefault()
            // The lift key held down auto-repeats; only a fresh press drops.
            if (!event.repeat) dropKeyboardDrag(drag)
            break
        case 'Escape':
            event.preventDefault()
            endGesture()
            announce('Drag cancelled.')
            break
        default:
            break
    }
}

/** Window keydown for every gesture kind: drives keyboard drags and lets Escape cancel pointer drags. */
export function onWindowKeyDown(event: KeyboardEvent): void {
    const running = runningGesture()
    if (running?.kind === 'keyboard') {
        // The lift keystroke itself bubbles up here too; the binding
        // preventDefaults it, so skip default-prevented events to avoid an
        // instant drop-after-lift on the same keypress.
        if (event.defaultPrevented) return
        onKeyboardGestureKey(running, event)
        return
    }
    if (event.key === 'Escape') endGesture()
}

/**
 * Window focusin: cancels a keyboard drag once focus lands outside it, for
 * example after Tab into an input. Otherwise the window keydown handler keeps
 * eating Space, Enter, and the arrow keys meant for that input.
 *
 * Focus inside the drag means the source, its drag handle, or a candidate the
 * engine focused. Focus lost to nothing (a removed element) fires no focusin,
 * so a re-render cannot cancel the drag.
 */
export function onWindowFocusIn(event: FocusEvent): void {
    const running = runningGesture()
    if (running?.kind !== 'keyboard') return
    const target = event.target
    if (!(target instanceof Element)) return
    if (isSourceOrHandle(target, running.sourceElement)) return
    if (target instanceof HTMLElement && running.candidates.includes(target)) return
    endGesture({ restoreFocus: false })
    announce('Drag cancelled.')
}
